import { motion, useReducedMotion } from "framer-motion";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AREAS, AREA_ORDER } from "@/components/discovery/areas";
import { trackSessionOnce } from "@/lib/analytics";

const Pill = ({ k, lit, burst }) => {
  const a = AREAS[k];
  const reduce = useReducedMotion();
  const pulse = lit && burst && !reduce;
  return (
    <span className="relative flex h-full w-full items-center justify-center gap-1.5 rounded-2xl px-2 py-1.5 text-center text-[10.5px] font-semibold leading-tight transition-[background-color,color,box-shadow] duration-700"
      style={lit
        ? { background: a.tint, color: a.color, boxShadow: `0 0 0 1px ${a.color}40, 0 0 14px -6px ${a.glow}` }
        : { background: "rgba(255,255,255,0.6)", color: "#64748B", boxShadow: "0 0 0 1px rgba(148,163,184,0.3)" }}>
      {pulse && (
        <motion.span key={burst} aria-hidden data-testid={`indicator-${k}-pulse`} className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{ boxShadow: `0 0 0 1.5px ${a.color}, 0 0 26px 2px ${a.glow}` }}
          initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ duration: 2, ease: "easeInOut" }} />
      )}
      <a.Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
      <span>{a.label}</span>
    </span>
  );
};

// Feature indicators: illuminate when Ellie connects a feature; tap for a short explanation.
export const DiscoveryIndicators = ({ found = [], discoveries = [], burst = null, className = "", source = "chat" }) => (
  <div className={`grid grid-cols-3 gap-2 ${className}`} data-testid="discovery-indicators">
    {AREA_ORDER.map((k) => {
      const a = AREAS[k];
      const lit = found.includes(k);
      const d = discoveries.find((x) => x.area === k);
      return (
        <Popover key={k}>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`indicator-${k}`} data-lit={lit ? "true" : "false"} aria-label={`About ${a.label}`}
              onClick={() => trackSessionOnce(`indicator_${k}_${source}`, "feature_indicator_opened", { area: k, lit, source })}
              className="rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
              <Pill k={k} lit={lit} burst={burst?.area === k ? burst.id : null} />
            </button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="center" sideOffset={10} data-testid={`indicator-popover-${k}`}
            className="relative w-[min(320px,calc(100vw-2rem))] rounded-3xl border-white/80 bg-white/95 p-5 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] backdrop-blur-xl">
            <PopoverPrimitive.Close data-testid={`indicator-popover-${k}-close`} aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <X className="h-4 w-4" />
            </PopoverPrimitive.Close>
            <div className="flex items-center gap-3 pr-8">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: `linear-gradient(135deg, ${a.color}, #6D5BD0)` }}>
                <a.Icon className="h-5 w-5" />
              </span>
              <p className="font-heading text-base font-bold text-slate-900">{a.label}</p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.about}</p>
            {lit && d?.line && (
              <div className="mt-3 rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed" style={{ background: a.tint, color: "#0F172A" }} data-testid={`indicator-popover-${k}-noticed`}>
                <span className="eyebrow block text-[9.5px]" style={{ color: a.color }}>Ellie noticed</span>
                {d.line}
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-400">Included with FioNova premium.</p>
          </PopoverContent>
        </Popover>
      );
    })}
  </div>
);
